import { useCallback, useContext, useRef } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import debounce from "lodash/debounce";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import ConfigContext from "context/ConfigContext";
import FinderResultsContext from "context/FinderResultsContext";
import { useSplitTreatment } from "hooks/useSplit";
import { getDomainCountryCode } from "redux/selectors/config";
import { localizeText } from "utils/localizeText";
import slugifyName from "utils/slugifyName";

import DispensaryCardPlaceholder from "components/botanic/DispensaryCard/DispensaryCardPlaceholder";
import Pagination from "components/Finder/Pagination";
import RecentlyViewedCarouselWithData from "components/Finder/RecentlyViewedCarousel";
import Sort from "components/Finder/Sort";
import NuggMdPlacement from "components/NuggMdPlacement/NuggMdPlacement";
import Placeholder, { Rect } from "components/Placeholder";

import NoResults from "./NoResults";
import StoreCard from "./StoreCard";

const FinderListings = () => {
  const topOfListings = useRef(null);

  const {
    sponsoredStores,
    stores,
    getPage,
    loadingStores,
    promotedNewStores,
    sortBy,
    storesCount,
  } = useContext(FinderResultsContext);

  const newFinderDeliveryTabEnabled = useSplitTreatment(
    "newFinderDeliveryTabEnabled"
  );
  const { retailType } = useContext(ConfigContext);
  const { query } = useRouter();
  const page: number = getPage(Number(query?.page) || 1);
  const scrollListener = () => {
    trackEvent("List", "Scroll List");
  };
  const countryCode = useSelector(getDomainCountryCode);
  const hasNoDeliveryResults =
    stores.length === 0 &&
    query?.filter?.includes("delivery") &&
    newFinderDeliveryTabEnabled;

  const keywords = (
    type: "dispensary" | "cbd-store" | "clinic",
    isPlural?: boolean
  ) => {
    switch (type) {
      case "dispensary":
        return isPlural
          ? localizeText("Dispensaries", countryCode)
          : localizeText("Dispensary", countryCode);
      case "cbd-store":
        return isPlural ? "CBD Stores" : "CBD Store";
      case "clinic":
        return isPlural ? "Doctors" : "Doctor";
      default:
        return "";
    }
  };

  const lazyScrollListener = useCallback(debounce(scrollListener, 500), []);
  const shouldShowSponsored =
    sponsoredStores.length > 0 &&
    page === 1 &&
    (sortBy?.display === "Recommended" || sortBy?.param === "default");

  if (loadingStores) {
    const numberOfSkelements = Array(20).fill(0);

    return (
      <div className="w-full pr-sm">
        <div className="flex flex-row items-center justify-between my-sm">
          <div className="text-xs ">Loading results</div>
          <Sort />
        </div>
        <div className="flex flex-row items-center justify-between">
          <Placeholder width="175px" height="18px" className="mt-lg mb-lg">
            <Rect width="100%" height="100%" />
          </Placeholder>
        </div>
        {numberOfSkelements.map((skele, idx) => (
          <div key={idx}>
            <DispensaryCardPlaceholder
              id={`dis-card-placeholder-${idx}`}
              className="mb-md"
              variant="platinum"
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full" onScroll={lazyScrollListener} ref={topOfListings}>
      {!hasNoDeliveryResults && (
        <div className="flex flex-row items-center justify-between my-sm">
          <div className="text-xs ">{storesCount} results</div>
          <Sort />
        </div>
      )}
      {retailType === "clinic" && <NuggMdPlacement />}

      {shouldShowSponsored && (
        <>
          <div className="flex flex-row justify-between">
            <h2 className="text-xs uppercase my-sm text-default">
              {`Sponsored ${keywords(retailType, true)}`}
            </h2>
          </div>
          <div className="flex flex-col">
            {sponsoredStores.map((store, i) => {
              return (
                <StoreCard
                  key={store.id}
                  store={store}
                  index={i}
                  id={`${slugifyName(store.name)}-listing-card`}
                  compact={false}
                />
              );
            })}
          </div>
        </>
      )}
      <RecentlyViewedCarouselWithData />
      {storesCount === 0 && <NoResults />}
      {stores.length > 0 && (
        <>
          <div className="flex flex-row items-center justify-between">
            <h2 className="text-xs uppercase text-default mt-lg mb-sm">
              {`All ${keywords(retailType)} results`}
            </h2>
          </div>
          <div className="flex flex-col">
            {promotedNewStores.concat(stores).map((store, i) => {
              return (
                <StoreCard
                  key={store.id}
                  store={store}
                  index={i}
                  id={`${slugifyName(store.name)}-listing-card`}
                  compact={false}
                  retailType={retailType}
                />
              );
            })}
          </div>
          <Pagination />
        </>
      )}
    </div>
  );
};

export default FinderListings;
